import Moment from "moment";

// Display formatters shared by every page. Amounts arrive from the API in
// rupees (decimal strings); pass integer paise through fromPaise first, or
// use <MoneyText paise />.

export const inr = (value, { compact = false, sign = false } = {}) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  // Non-breaking spaces keep the sign, symbol and amount on one line.
  const prefix = sign && n > 0 ? "+\u00a0" : n < 0 ? "−\u00a0" : "";
  const abs = Math.abs(n);
  if (compact && abs >= 100000) {
    const lakh = abs / 100000;
    return prefix + "₹\u00a0" + (lakh >= 100 ? (lakh / 100).toFixed(2) + " Cr" : lakh.toFixed(2) + " L");
  }
  return prefix + "₹\u00a0" + abs.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fromPaise = (paise) => (paise === null || paise === undefined || paise === "" ? paise : Number(paise) / 100);

export const count = (n) => Number(n || 0).toLocaleString("en-IN");

export const dateTime = (v) => (v ? Moment(v).format("D MMM YYYY, h:mm A") : "");

export const dateOnly = (v) => (v ? Moment(v).format("D MMM YYYY") : "");

// 'YYYY-MM-DD' (the format every date filter in the API expects).
export const ymd = (v) => (v ? Moment(v).format("YYYY-MM-DD") : "");

// "3h ago", "2d ago"; with the exact time in a title tooltip at the call site.
export const timeAgo = (v) => {
  if (!v) return "";
  const m = Moment(v);
  const mins = Moment().diff(m, "minutes");
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days}d ago` : m.format("D MMM YYYY");
};

export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/* Indian financial year (April to March), e.g. "2026-27". */

export const fyOf = (v = new Date()) => {
  const m = Moment(v);
  const start = m.month() >= 3 ? m.year() : m.year() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
};

// { from, to } as 'YYYY-MM-DD' for an FY string like "2026-27".
export const fyRange = (fy) => {
  const start = parseInt(String(fy).slice(0, 4), 10);
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
};

// Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
export const quarterOf = (v = new Date()) => {
  const month = Moment(v).month();
  return month >= 3 ? Math.floor((month - 3) / 3) + 1 : 4;
};

export const QUARTER_LABELS = { 1: "Q1 · Apr–Jun", 2: "Q2 · Jul–Sep", 3: "Q3 · Oct–Dec", 4: "Q4 · Jan–Mar" };

// A CSV cell a spreadsheet would run as a formula (=, +, -, @, tab or CR
// first) gets a leading apostrophe. Numbers, and text that is a plain number
// (also a negative one), are left alone.
export function csvSafe(value) {
  if (typeof value !== "string") return value;
  if (/^-?\d+(\.\d+)?$/.test(value.trim())) return value;
  return /^\s*[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

// "1 order", "3 orders", "2 users" (count formatted). many defaults to one + "s".
export const plural = (n, one, many) => `${count(n)} ${Number(n) === 1 ? one : many || one + "s"}`;
